import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
  TEXTAREA_CONTAINER_STYLES,
} from "../styles"

interface RepeatsItselfConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const EXAMPLE_PROMPTS = [
  `Check if the {target_role_name} repeats the same question or request more than once in this conversation: {transcript}`,
  `Identify if the {target_role_name} gives the same troubleshooting step multiple times: {transcript}`,
  `Does the {target_role_name} repeat any apology or greeting phrase verbatim? {transcript}`,
]

export const RepeatsItselfConfig: React.FC<RepeatsItselfConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const metadata = metricRecordData.metric_metadata
  const hasRegexPattern = !!metadata?.regex_pattern

  const updateMetadata = (updates: Record<string, unknown>) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metadata,
        ...updates,
      },
    })
  }

  return (
    <div className="space-y-4">
      {/* Custom Prompt (optional) */}
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Custom Prompt (optional)</label>
        </div>
        <div className={TEXTAREA_CONTAINER_STYLES}>
          <textarea
            className="w-full min-h-[80px] text-sm text-foreground bg-transparent border-none focus:outline-none resize-none placeholder:text-coval-text-muted"
            value={metadata?.custom_prompt || ""}
            onChange={(e) => updateMetadata({ custom_prompt: e.target.value })}
            placeholder="Leave empty to use the default repetition detection prompt."
          />
          {!metadata?.custom_prompt && (
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {EXAMPLE_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="rounded-md border border-coval-border bg-surface-1 px-2.5 py-1.5 text-left text-xs text-coval-text-muted transition-colors hover:border-coval-border hover:bg-surface-1"
                  onClick={() => updateMetadata({ custom_prompt: prompt })}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-xs text-coval-text-muted">
          Customize how the LLM judge evaluates repetitions. Use{" "}
          {"{transcript}"} for the conversation, {"{target_role_name}"} for the
          speaker role, and {"{regex_analysis}"} for the regex findings.
        </p>
      </div>

      {/* Regex Pattern (optional) */}
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Regex Pattern (optional)</label>
        </div>
        <input
          type="text"
          className={TEXT_INPUT_STYLES}
          value={metadata?.regex_pattern || ""}
          onChange={(e) => updateMetadata({ regex_pattern: e.target.value })}
          placeholder="(.*)"
        />
        <p className="text-xs text-coval-text-muted">
          {hasRegexPattern
            ? "Regex extracts content from each message for similarity comparison before LLM analysis."
            : "Leave empty to use LLM judge only. Provide a pattern to enable regex pre-analysis."}
        </p>
      </div>

      {/* Regex settings - only shown when a regex pattern is provided */}
      {hasRegexPattern && (
        <>
          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>Similarity Threshold</label>
            </div>
            <input
              type="number"
              step="0.05"
              min="0.01"
              max="1"
              className={TEXT_INPUT_STYLES}
              value={metadata?.similarity_threshold ?? 0.85}
              onChange={(e) => {
                const value = parseFloat(e.target.value)
                updateMetadata({
                  similarity_threshold: isNaN(value) ? undefined : value,
                })
              }}
              placeholder="0.85"
            />
            <p className="text-xs text-coval-text-muted">
              Jaccard similarity threshold (0-1) for consecutive messages to be
              considered repetitions.
            </p>
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>Min Repetition Count</label>
            </div>
            <input
              type="number"
              step="1"
              min="2"
              className={TEXT_INPUT_STYLES}
              value={metadata?.min_repetition_count ?? 2}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10)
                updateMetadata({
                  min_repetition_count: isNaN(value) ? undefined : value,
                })
              }}
              placeholder="2"
            />
            <p className="text-xs text-coval-text-muted">
              Minimum number of consecutive similar message pairs to flag as
              repetition.
            </p>
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <label className={LABEL_STYLES}>Case Insensitive</label>
            </div>
            <Select
              value={metadata?.case_insensitive ? "true" : "false"}
              onValueChange={(value) =>
                updateMetadata({ case_insensitive: value === "true" })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">No</SelectItem>
                <SelectItem value="true">Yes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    </div>
  )
}
